import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { TopSection } from '../../ui/TopSection';
import { useLoadPriceAlerts, useLoadOrderHistory } from '../../../utils/dataLoadingUtils';
import { usePriceAlertsStore } from '../../../stores/appPriceAlertsStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useAppUserDefaultStore, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { StatCard, formatMainValue } from '../../ui/StatCard';
import { filterPriceAlertsByDateRange } from '../../../utils/dateRangeFilters';
import { HorizontalGrid } from '../../ui/layout/HorizontalGrid';
import { baseTextSize, borderPrimary, extraLargeTextSize, placeholderPrimary, primaryText, semiBoldText, smallTextSize, tertiaryText, whiteBG, iconBgBlue, iconBgPurple, iconBgRed, iconBgAmber } from '../../../styling/stylingHelper';
import { PriceAlertComponent } from './PriceAlertComponent';
import { usePriceAlertFilters } from '../../../hooks/usePriceAlertFilters';
import { Search, Bell, ArrowUpDown, Wallet, Percent } from 'lucide-react';
import { PlatformStatistics } from './PlatformStatistics';
import { calculatePlatformStatistics, getAvailableCategories, filterPriceAlertsByOverallType, calculateActualMoneyImpact, calculateWeightedAveragePriceChange } from '../../../utils/priceAlertUtils';
import { filterAllPriceAlerts } from '../../../utils/filterUtils';
import { resetScrollToTop } from '../../../utils/scrollUtils';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useFuzzySearch } from '../../../hooks/useFuzzySearch';
import { useAuth } from '../../../services/authentication/authContext';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';

// Mobile-only: how many alert cards to render before "Load more" (keeps the DOM
// small on phones; desktop still renders the full list — see the render below).
const MOBILE_ALERTS_STEP = 20;

export interface PlatformStats {
    platform: string;
    totalAlerts: number;
    priceIncreases: number;
    priceDecreases: number;
    avgPriceChange: number;
    maxPriceChange: number;
    minPriceChange: number;
  }

const PriceAlertsPage = () => {
  const { t } = useTranslation();
  const { accountSettings } = useAuth();
  const isDemoAccount = accountSettings.includes('demo_account');

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Load data. Order history (a secondary input for the change/impact KPIs) loads
  // progressively below, after the time controls are known.
  useLoadPriceAlerts();

  // Connect to stores
  const priceAlertsStore = usePriceAlertsStore();
  const orderHistoryStore = useOrderHistory();
  const allAlerts = priceAlertsStore.priceAlerts;
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);

  // Get loading state from store
  const isPriceAlertsLoading = usePriceAlertsStore(s => s.isLoading);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks', 'non-food'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Price alert filters hook
  const {
    filters,
    setPlatformFilters,
    setSectionFilters,
    setCategoryFilters,
    setChangeType,
    resetFilters: resetPriceAlertFilters,
    activeFilterCount: baseActiveFilterCount,
  } = usePriceAlertFilters({
    includePlatforms: true,
    includeSections: true,
    includeCategories: true,
  });

  // Calculate total active filter count including restaurant filters
  const activeFilterCount = useMemo(() => {
    let count = baseActiveFilterCount;
    if (isAllRestaurants && restaurantFilters.length > 0) {
      count++;
    }
    return count;
  }, [baseActiveFilterCount, isAllRestaurants, restaurantFilters]);

  // Wrapper function to reset all filters including restaurant and section filters
  const resetFilters = () => {
    resetPriceAlertFilters(); // Resets price alert platform and category filters
    // Reset shared filters (section filters are shared between pages)
    if (setSectionFilters) {
      setSectionFilters([]);
    }
    if (isAllRestaurants && setRestaurantFilters) {
      setRestaurantFilters([]);
    }
  };

  // local states
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Mobile "Load more" window size (see MOBILE_ALERTS_STEP).
  const [mobileVisibleCount, setMobileVisibleCount] = useState<number>(MOBILE_ALERTS_STEP);

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load order history progressively: the selected window (default last week) renders
  // first, then the rest of the history streams in the background.
  useLoadOrderHistory({ progressive: true, viewStart: startDate });

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const ordersLoadedFromDate = useOrderHistory(s => s.loadedFromDate);
  const isOrderHistoryLoading =
    !ordersFullyLoaded && (
      !ordersFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!ordersLoadedFromDate && startDate < ordersLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredPriceAlerts = useMemo(() =>
    filterPriceAlertsByDateRange(allAlerts, startDate, endDate),
    [allAlerts, startDate, endDate]
  );

  // Apply overall type filter
  const overallTypeFilteredPriceAlerts = useMemo(() =>
    filterPriceAlertsByOverallType(dateFilteredPriceAlerts, overallTypeFilter),
    [dateFilteredPriceAlerts, overallTypeFilter]
  );

  // Apply all filters (restaurants, platforms, and categories)
  const filterAppliedPriceAlerts = useMemo(() =>
    filterAllPriceAlerts(
      overallTypeFilteredPriceAlerts,
      filters,
      isAllRestaurants ? restaurantFilters : undefined,
      platformToSectionIndex
    ),
    [overallTypeFilteredPriceAlerts, filters, isAllRestaurants, restaurantFilters, platformToSectionIndex]
  );

  // Apply fuzzy search query filter
  const filteredPriceAlerts = useFuzzySearch({
    data: filterAppliedPriceAlerts,
    keys: ['productName'],
    query: searchQuery,
    threshold: 0.4,
  });

  // Reset the mobile "Load more" window whenever the result set size changes
  // (a filter / search / date change) so the phone list starts fresh from the top.
  useEffect(() => {
    setMobileVisibleCount(MOBILE_ALERTS_STEP);
  }, [filteredPriceAlerts.length]);

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'non-food');
  };

  // Handler for VAT toggle - updates for orders VAT setting
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await Promise.all([
        saveVatOrdersSetting(showExVat)
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available sections from the restaurant store
  const availableSections = useMemo(() => {
    if (!restaurantId || !restaurantSectionsMap[restaurantId]) return [];
    return restaurantSectionsMap[restaurantId].map(s => ({ id: s.id, name: s.name }));
  }, [restaurantId, restaurantSectionsMap]);

  // Get available filter options from the overall-type-filtered data (before applying other filters)
  const availablePlatforms = useMemo(() => {
    const platforms = new Set<string>();
    overallTypeFilteredPriceAlerts.forEach(alert => {
      if (alert.platform) {
        platforms.add(alert.platform);
      }
    });
    return Array.from(platforms).sort();
  }, [overallTypeFilteredPriceAlerts]);

  const availableCategories = useMemo(() =>
    getAvailableCategories(overallTypeFilteredPriceAlerts),
    [overallTypeFilteredPriceAlerts]
  );

  // get stats for the simple cards
  const totalAlerts = filteredPriceAlerts.length;
  const priceIncreases = useMemo(() => 
    filteredPriceAlerts.filter(alert => alert.toPrice > alert.fromPrice).length,
    [filteredPriceAlerts]
  );
  const priceDecreases = useMemo(() => 
    filteredPriceAlerts.filter(alert => alert.toPrice < alert.fromPrice).length,
    [filteredPriceAlerts]
  );
  const avgPriceChange = useMemo(() => 
    calculateWeightedAveragePriceChange(filteredPriceAlerts, orderHistoryStore),
    [filteredPriceAlerts, orderHistoryStore]
  );
  const actualMoneyImpact = useMemo(() => 
    calculateActualMoneyImpact(filteredPriceAlerts, orderHistoryStore),
    [filteredPriceAlerts, orderHistoryStore]
  );

  // Calculate platform statistics
  const platformStatistics = useMemo(() =>
    calculatePlatformStatistics(filteredPriceAlerts),
    [filteredPriceAlerts]
  );

  // Build platformId → restaurantName map for "All Restaurants" view
  const platformIdToRestaurantName = useMemo(() => {
    if (!isAllRestaurants) return undefined;
    const restaurantNameById = new Map(restaurants.map(r => [r.id, r.name]));
    const map = new Map<string, string>();
    Object.entries(platformToSectionIndex).forEach(([platformId, section]) => {
      const name = restaurantNameById.get(section.restaurantId);
      if (name) map.set(platformId, name);
    });
    return map;
  }, [isAllRestaurants, restaurants, platformToSectionIndex]);

  return (
    <div>
      <MobilePageHeader title={t('priceAlerts.title')} right={<RestaurantSelector />} />

      <TopSection
        title={t('priceAlerts.title')}
        backButton={false}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        onOverallTypeChange={handleOverallTypeChange}
        currentOverallTypeValue={overallTypeFilter}
        overallTypeOptions={['all', 'food', 'drinks', 'non-food']}
        onVatChange={handleVatChange}
        currentVatValue={showExVatOrders ? 'excl' : 'incl'}
        showFilters={true}
        filters={{
          priceAlertFilters: {
            restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
            setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
            availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
            sectionFilters: filters.sections,
            setSectionFilters,
            availableSections,
            platformFilters: filters.platforms,
            setPlatformFilters,
            availablePlatforms,
            categoryFilters: filters.categories,
            setCategoryFilters,
            availableCategories,
            changeType: filters.changeType,
            setChangeType,
            onResetFilters: resetFilters,
            activeFilterCount,
          }
        }}
      />

      {/* Responsive layout group for content sections below 1250px */}
      <div className="mt-4 mb-4 flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below — 2-col compact grid on phones */}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-col sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            icon={<Bell className="w-6 h-6" style={{ color: '#2E90FA' }} />}
            iconBg={iconBgBlue}
            title={t('priceAlerts.totalAlerts')}
            value={totalAlerts}
            valueType='number'
            loading={isPriceAlertsLoading}
            mobileLayout="tile"
          />
          </div>
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            icon={<ArrowUpDown className="w-6 h-6" style={{ color: '#6938EF' }} />}
            iconBg={iconBgPurple}
            title={t('priceAlerts.changes')}
            value={priceIncreases === 0 && priceDecreases === 0 ? '--' : `↑ ${priceIncreases} / ↓ ${priceDecreases}`}
            loading={isPriceAlertsLoading || isOrderHistoryLoading}
            mobileLayout="tile"
          />
          </div>
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            icon={<Wallet className="w-6 h-6" style={{ color: '#D92D20' }} />}
            iconBg={iconBgRed}
            title={t('priceAlerts.actualChange')}
            value={(() => {
              const formatted = formatMainValue(actualMoneyImpact, 'currency');
              return actualMoneyImpact > 0 && formatted !== '--' ? `+${formatted}` : formatted;
            })()}
            loading={isPriceAlertsLoading || isOrderHistoryLoading}
            mobileLayout="tile"
          />
          </div>
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />}
            iconBg={iconBgAmber}
            title={t('priceAlerts.avgChange')}
            value={avgPriceChange}
            valueType='percentage'
            loading={isPriceAlertsLoading}
            mobileLayout="tile"
          />
          </div>
        </div>

        {/* Platform Statistics */}
        <div>
          <PlatformStatistics statistics={platformStatistics} />
        </div>

        <div>
            <HorizontalGrid>
                <div className={cn(semiBoldText, primaryText, extraLargeTextSize, "pt-1 pb-1")}>
                    {t('priceAlerts.recentChanges')}
                </div>
                <div className="flex justify-end">
                    <div className="relative w-full max-w-[400px]" style={{ marginLeft: 'auto' }}>
                        <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none", tertiaryText)} />
                        <input
                            type="text"
                            placeholder={t('priceAlerts.searchByProductName')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={cn(
                                "w-full pl-10 pr-4 py-2 rounded-xl border",
                                borderPrimary, whiteBG, primaryText, placeholderPrimary,
                                "focus:outline-none focus:ring-1 focus:ring-darkGreenColor dark:focus:ring-darkGreenColor-dark"
                            )}
                        />
                    </div>
                </div>
            </HorizontalGrid>
        </div>

        {/* All alerts showing component here */}
        <div>
            {filteredPriceAlerts.length === 0 ? (
                <div className={cn(baseTextSize, tertiaryText, "text-center py-10")}>
                    {t('priceAlerts.noAlertsInRange')}
                </div>
            ) : (
                <>
                    {/* Desktop: full list, unchanged. */}
                    <div className="hidden sm:block">
                        {filteredPriceAlerts.map((alert) => (
                            <div className="mb-3" key={alert.id}>
                                <PriceAlertComponent alert={alert} isDemoAccount={isDemoAccount} restaurantName={platformIdToRestaurantName?.get(alert.restaurantPlatformId)} />
                            </div>
                        ))}
                    </div>
                    {/* Mobile: truly windowed — only the visible slice is mounted,
                        so the phone DOM never holds the whole list. */}
                    <div className="sm:hidden">
                        {filteredPriceAlerts.slice(0, mobileVisibleCount).map((alert) => (
                            <div className="mb-3" key={alert.id}>
                                <PriceAlertComponent alert={alert} isDemoAccount={isDemoAccount} restaurantName={platformIdToRestaurantName?.get(alert.restaurantPlatformId)} />
                            </div>
                        ))}
                        {mobileVisibleCount < filteredPriceAlerts.length && (
                            <button
                                type="button"
                                onClick={() => setMobileVisibleCount((c) => c + MOBILE_ALERTS_STEP)}
                                className={cn(smallTextSize, semiBoldText, 'w-full py-2 rounded-xl border', borderPrimary, primaryText)}
                            >
                                {t('common.loadMore')}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
      </div>
    </div>
  );
};

export default PriceAlertsPage;